<template>
  <div class="invite-link-form">
    <!-- Link Active State -->
    <div v-if="link" class="link-active">
      <div class="link-input-group">
        <label class="form-label">{{ activeTab }}</label>
        <div class="link-input-wrapper">
          <input
            class="link-input"
            type="text"
            readonly
            :value="link"
            @focus="$event.target.select()"
            ref="link"
          />
          <button class="copy-btn" @click="copyInviteLink" title="Copy link">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="6" y="6" width="9.75" height="9.75" rx="2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12 6V4.5C12 3.67157 11.3284 3 10.5 3H4.5C3.67157 3 3 3.67157 3 4.5V10.5C3 11.3284 3.67157 12 4.5 12H6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Valid link actions -->
      <div v-if="linkValidTime !== 'EXPIRED'" class="link-actions">
        <button class="action-btn action-btn--danger" @click="deactivateLink" :disabled="btnLoading">
          <svg v-if="!(btnLoading && loadingTrigger === 'deactivate')" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else class="spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 1.5V4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 12V14.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4"/>
            <path d="M3.40381 3.40381L5.17881 5.17881" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10.8213 10.8212L12.5963 12.5962" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4"/>
          </svg>
          <span v-if="!(btnLoading && loadingTrigger === 'deactivate')">Deactivate</span>
        </button>
        <button class="action-btn action-btn--primary" @click="generateLink(true)" :disabled="btnLoading">
          <svg v-if="!(btnLoading && loadingTrigger === 'genLink')" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M4.66666 6.66667L8 10L11.3333 6.66667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 10V2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else class="spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 1.5V4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 12V14.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4"/>
            <path d="M3.40381 3.40381L5.17881 5.17881" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10.8213 10.8212L12.5963 12.5962" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4"/>
          </svg>
          <span v-if="!(btnLoading && loadingTrigger === 'genLink')">Generate New</span>
        </button>
      </div>

      <!-- Expired link state -->
      <div v-if="linkValidTime === 'EXPIRED'" class="expired-state">
        <div class="expired-badge">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 5.33334V8.00001" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 10.6667H8.00667" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>This link has expired</span>
        </div>
        <button class="generate-btn" :disabled="loading" @click="$emit('gen-link')">
          <svg v-if="!loading" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.75 9C15.75 12.7279 12.7279 15.75 9 15.75C5.27208 15.75 2.25 12.7279 2.25 9C2.25 5.27208 5.27208 2.25 9 2.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 3V6H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M15 3L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else class="spinner" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 2V5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9 13V16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4"/>
          </svg>
          <span v-if="!loading">Generate New Link</span>
        </button>
      </div>
    </div>

    <!-- Link Deactivated State -->
    <div v-if="!link" class="link-deactivated">
      <div class="deactivated-icon">
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 25.6667C20.4433 25.6667 25.6667 20.4433 25.6667 14C25.6667 7.55668 20.4433 2.33334 14 2.33334C7.55668 2.33334 2.33334 7.55668 2.33334 14C2.33334 20.4433 7.55668 25.6667 14 25.6667Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M17.5 10.5L10.5 17.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10.5 10.5L17.5 17.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h4 class="deactivated-title">Link Deactivated</h4>
      <p class="deactivated-text">This invite link is no longer active. Generate a new link to invite members.</p>
      <button class="generate-btn" @click="generateLink" :disabled="btnLoading">
        <svg v-if="!(btnLoading && loadingTrigger === 'genLink')" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.75 9C15.75 12.7279 12.7279 15.75 9 15.75C5.27208 15.75 2.25 12.7279 2.25 9C2.25 5.27208 5.27208 2.25 9 2.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 3V6H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M15 3L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else class="spinner" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 2V5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9 13V16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4"/>
        </svg>
        <span v-if="!(btnLoading && loadingTrigger === 'genLink')">Generate New Link</span>
      </button>
    </div>
  </div>
</template>

<script>
  import { mapActions } from "vuex";

  export default {
    name: "InviteLinkForm",

    emits: ["deactivate", "gen-link"],

    data() {
      return {
        loadingTrigger: null,
      };
    },

    props: {
      link: { type: String, default: () => "", required: true },
      linkValidTime: { type: String, default: () => "", required: true },
      activeTab: { type: String, default: () => "", required: true },
      btnLoading: { type: Boolean, default: () => false, required: true },
      loading: { type: Boolean, default: () => false },
    },

    methods: {
      ...mapActions(["showToast"]),

      copyInviteLink() {
        this.$refs.link.focus();

        // TODO: Look for an alternative to execCommand
        document.execCommand("copy");
        this.showToast({
          description: "Link copied",
          display: true,
          type: "success",
        });
      },
      generateLink(regenerate = false) {
        this.loadingTrigger = "genLink";
        this.$emit("gen-link", regenerate);
      },
      deactivateLink() {
        this.loadingTrigger = "deactivate";
        this.$emit("deactivate");
      },
    },
  };
</script>

<style lang="scss" scoped>
.invite-link-form {
  width: 100%;
}

.link-active {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.link-input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #0F172A;
}

.link-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  height: 48px;
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: #4D84FF;
    background: white;
  }
}

.link-input {
  flex: 1;
  min-width: 0;
  padding: 0;
  background: transparent;
  border: none;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #0F172A;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus {
    outline: none;
  }
}

.copy-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 6px;
  color: #64748B;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #4D84FF;
    border-color: #4D84FF;
    background: #EEF2FF;
  }
}

.link-actions {
  display: flex;
  gap: 12px;
}

.action-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &--danger {
    background: #FEF2F2;
    border: 1px solid #FECACA;
    color: #EF4444;

    &:hover:not(:disabled) {
      background: #FEE2E2;
    }
  }

  &--primary {
    background: #4D84FF;
    border: none;
    color: white;

    &:hover:not(:disabled) {
      background: #3B6EE8;
    }
  }
}

.expired-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
}

.expired-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #FEF3C7;
  border-radius: 20px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #D97706;
}

.link-deactivated {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 16px;
}

.deactivated-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: #FEF2F2;
  border-radius: 50%;
  color: #EF4444;
  margin-bottom: 16px;
}

.deactivated-title {
  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #0F172A;
  margin: 0 0 8px 0;
}

.deactivated-text {
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #64748B;
  margin: 0 0 20px 0;
  max-width: 280px;
  line-height: 1.5;
}

.generate-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: #4D84FF;
  border: none;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #3B6EE8;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
